import React from "react";
import {
  StyleSheet,
  View,
  Text,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import { LineChart, BarChart, PieChart } from "react-native-chart-kit";

/**
 * Displays sales data in a chart format
 *
 * @param {object} props
 * @param {string} props.type - The type of chart ('line' or 'bar')
 * @param {Array} props.data - The data to display in the chart
 * @param {Array} props.labels - Labels for the data points
 * @param {string} props.title - Chart title
 * @param {boolean} props.isLoading - Whether the data is loading
 * @param {object} props.style - Additional style for the container
 * @param {object} props.chartConfig - Additional chart configuration options
 */
const SalesChart = ({
  type = "line",
  data = [],
  labels = [],
  title,
  isLoading = false,
  style,
  chartConfig,
}) => {
  const screenWidth = Dimensions.get("window").width - 40; // With some padding

  const defaultChartConfig = {
    backgroundColor: "#fff",
    backgroundGradientFrom: "#fff",
    backgroundGradientTo: "#fff",
    decimalPlaces: 0,
    color: (opacity = 1) => `rgba(52, 152, 219, ${opacity})`,
    labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
    style: {
      borderRadius: 16,
    },
    propsForDots: {
      r: "5",
      strokeWidth: "2",
      stroke: "#3498db",
    },
    propsForLabels: {
      fontSize: 10,
    },
  };

  const combinedChartConfig = {
    ...defaultChartConfig,
    ...chartConfig,
  };

  const chartData = {
    labels,
    datasets: [
      {
        data,
        color: (opacity = 1) => `rgba(52, 152, 219, ${opacity})`,
        strokeWidth: 2,
      },
    ],
  };

  // Prepare pie chart data format
  const pieChartData = labels.map((label, index) => ({
    name: label,
    population: data[index] || 0,
    color: `hsl(${(index * 50) % 360}, 70%, 50%)`, // generates different colors
    legendFontColor: "#2c3e50",
    legendFontSize: 12,
  }));

  if (isLoading) {
    return (
      <View style={[styles.container, styles.loadingContainer, style]}>
        <ActivityIndicator size="large" color="#3498db" />
        <Text style={styles.loadingText}>Loading chart data...</Text>
      </View>
    );
  }

  if (!data || data.length === 0) {
    return (
      <View style={[styles.container, styles.emptyContainer, style]}>
        <Text style={styles.emptyText}>No data available</Text>
        <Text style={styles.emptySubtext}>
          There is no data to display for the selected time period.
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, style]}>
      {title && <Text style={styles.title}>{title}</Text>}

      {type === "line" ? (
        <LineChart
          data={chartData}
          width={screenWidth}
          height={220}
          chartConfig={combinedChartConfig}
          bezier
          style={styles.chart}
          fromZero
        />
      ) : type === "bar" ? (
        <BarChart
          data={chartData}
          width={screenWidth}
          height={220}
          chartConfig={combinedChartConfig}
          style={styles.chart}
          fromZero
          showValuesOnTopOfBars
        />
      ) : (
        <PieChart
          data={pieChartData}
          width={screenWidth}
          height={100}
          chartConfig={combinedChartConfig}
          accessor={"population"}
          backgroundColor={"transparent"}
          paddingLeft={"2"}
          style={styles.chart}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
    marginVertical: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2c3e50",
    marginBottom: 12,
  },
  chart: {
    borderRadius: 8,
    marginVertical: 8,
  },
  loadingContainer: {
    height: 250,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#7f8c8d",
  },
  emptyContainer: {
    height: 200,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#7f8c8d",
  },
  emptySubtext: {
    fontSize: 14,
    color: "#95a5a6",
    textAlign: "center",
    marginTop: 8,
    maxWidth: "80%",
  },
});

export default SalesChart;
